import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from '@/hooks/useAuth'

// Partners are scoped to their own org server-side (no id needed → 'self'); admins pass the org
// they're viewing so each org gets its own cache entry rather than one global blob.
export const peopleDirectoryQueryKey = (organizationId?: string) =>
  ['peopleDirectory', organizationId ?? 'self'] as const

export const fetchPeopleDirectory = async (
  organizationId?: string,
): Promise<UserService.PersonDirectoryEntry[]> => {
  const response = await apis.userService.personsDirectoryGet(organizationId)
  return response.data ?? []
}

export const usePeopleDirectory = (organizationId?: string) => {
  const { isPartner, isAdmin } = useAuth()
  const query = useQuery({
    queryKey: peopleDirectoryQueryKey(organizationId),
    queryFn: () => fetchPeopleDirectory(organizationId),
    // Partners always fetch their own org. Admins must wait until the org is known (resolved from
    // the cohort) so they load that org's people, not every person in the system.
    enabled: isPartner || (isAdmin && organizationId !== undefined && organizationId !== ''),
    staleTime: 30 * 60 * 1000,
  })
  const directoryById = useMemo(() => {
    const map = new Map<string, UserService.PersonDirectoryEntry>()
    for (const entry of query.data ?? []) map.set(entry.id, entry)
    return map
  }, [query.data])
  return {
    directory: query.data ?? [],
    directoryById,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}
